/**
 * 🔔 ADMIN NOTIFICATIONS PAGE
 * Complete notification management dashboard with templates, sending, analytics, and history
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Send as SendIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  TrendingUp,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  adminNotificationService,
  type NotificationTemplate,
  type NotificationHistoryItem,
  type EnhancedNotificationStats,
  type NotificationListItem,
} from '@/services/adminNotificationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminNotificationsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Send Notification State
  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    targetType: 'all_hosts' as 'all_hosts' | 'all_renters' | 'specific',
    userIds: '',
    type: 'custom',
  });
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Send to Single User State
  const [singleUserForm, setSingleUserForm] = useState({
    userId: '',
    email: '',
    phoneNumber: '',
    title: '',
    message: '',
    type: 'custom',
  });
  const [sendingSingleUser, setSendingSingleUser] = useState(false);

  // Templates State
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    titleTemplate: '',
    messageTemplate: '',
    isActive: true,
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    channels: {
      push: true,
      email: false,
      sms: false,
    },
    emailSubjectTemplate: '',
    emailHtmlTemplate: '',
    smsTemplate: '',
  });
  const [saving, setSaving] = useState(false);

  // Analytics State (Enhanced)
  const [enhancedStats, setEnhancedStats] = useState<EnhancedNotificationStats | null>(null);

  // History State (Now using notification list)
  const [notificationsList, setNotificationsList] = useState<NotificationListItem[]>([]);
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(50);
  const [listTotal, setListTotal] = useState(0);
  const [listSummary, setListSummary] = useState({ total: 0, read: 0, unread: 0, readRate: 0 });
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);

  // Load data on mount
  useEffect(() => {
    if (activeTab === 1) loadTemplates();
    if (activeTab === 2) loadAnalytics();
    if (activeTab === 3) loadHistory();
  }, [activeTab]);

  // ========================================
  // SEND NOTIFICATION FUNCTIONS
  // ========================================

  const handleSendNotification = async () => {
    if (!sendForm.title || !sendForm.message) {
      toast.error('Please fill in title and message');
      return;
    }

    setSending(true);
    try {
      let userIds: string[] | 'all_hosts' | 'all_renters';

      if (sendForm.targetType === 'specific') {
        userIds = sendForm.userIds.split(',').map(id => id.trim()).filter(id => id);
        if (userIds.length === 0) {
          toast.error('Please enter at least one user ID');
          setSending(false);
          return;
        }
      } else {
        userIds = sendForm.targetType;
      }

      const result = await adminNotificationService.sendNotification({
        userIds,
        title: sendForm.title,
        message: sendForm.message,
        type: sendForm.type,
      });

      if (result.success) {
        toast.success(`✅ Notification sent to ${result.data.sent} users!`);
        // Clear form
        setSendForm({
          title: '',
          message: '',
          targetType: 'all_hosts',
          userIds: '',
          type: 'custom',
        });
        // Refresh history if on that tab
        if (activeTab === 3) loadHistory();
      } else {
        toast.error(result.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const getEstimatedRecipients = (): string => {
    if (sendForm.targetType === 'all_hosts') return 'All Hosts (~1,234)';
    if (sendForm.targetType === 'all_renters') return 'All Renters (~5,678)';
    if (sendForm.targetType === 'specific') {
      const count = sendForm.userIds.split(',').filter(id => id.trim()).length;
      return `${count} specific user${count !== 1 ? 's' : ''}`;
    }
    return '0';
  };

  const handleSendToSingleUser = async () => {
    // Validate: at least one identifier must be provided
    if (!singleUserForm.userId && !singleUserForm.email && !singleUserForm.phoneNumber) {
      toast.error('Please provide at least one: User ID, Email, or Phone Number');
      return;
    }

    if (!singleUserForm.title || !singleUserForm.message) {
      toast.error('Please fill in title and message');
      return;
    }

    setSendingSingleUser(true);
    try {
      const result = await adminNotificationService.sendToUser({
        userId: singleUserForm.userId || undefined,
        email: singleUserForm.email || undefined,
        phoneNumber: singleUserForm.phoneNumber || undefined,
        title: singleUserForm.title,
        message: singleUserForm.message,
        type: singleUserForm.type,
      });


      if (result.success) {
        toast.success(
          `✅ Notification sent to ${result.data.sentTo.name}!\n` +
          `Identified by: ${result.data.identifierUsed}`
        );
        // Clear form
        setSingleUserForm({
          userId: '',
          email: '',
          phoneNumber: '',
          title: '',
          message: '',
          type: 'custom',
        });
        // Refresh history if on that tab
        if (activeTab === 3) loadHistory();
      } else {
        console.error('❌ Send failed:', result.error);
        toast.error(result.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('❌ Error sending notification to user:', error);
      toast.error('Failed to send notification');
    } finally {
      setSendingSingleUser(false);
    }
  };

  // ========================================
  // TEMPLATE FUNCTIONS
  // ========================================

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await adminNotificationService.getTemplates();
      if (result.success) {
        setTemplates(result.data.templates);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeTemplates = async () => {
    if (!confirm('Initialize notification templates? This will add 5 new templates including booking_pending_approval, booking_approved, and booking_declined.')) {
      return;
    }

    setInitializing(true);
    try {
      toast.loading('Initializing templates...', { id: 'init-templates' });
      const result = await adminNotificationService.initializeTemplates();

      if (result.success) {
        toast.success('✅ Templates initialized successfully! Reloading...', { id: 'init-templates' });
        // Reload templates to show the new ones
        await loadTemplates();
      } else {
        toast.error(result.error || 'Failed to initialize templates', { id: 'init-templates' });
      }
    } catch (error) {
      console.error('Error initializing templates:', error);
      toast.error('Failed to initialize templates', { id: 'init-templates' });
    } finally {
      setInitializing(false);
    }
  };

  const handleEditTemplate = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setEditForm({
      titleTemplate: template.titleTemplate,
      messageTemplate: template.messageTemplate,
      isActive: template.isActive,
      description: template.description,
      priority: template.priority || 'normal',
      channels: template.channels || { push: true, email: false, sms: false },
      emailSubjectTemplate: template.emailSubjectTemplate || '',
      emailHtmlTemplate: template.emailHtmlTemplate || '',
      smsTemplate: template.smsTemplate || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const result = await adminNotificationService.updateTemplate(
        selectedTemplate.type,
        editForm
      );

      if (result.success) {
        toast.success('✅ Template updated successfully!');
        setEditDialogOpen(false);
        loadTemplates(); // Reload templates
      } else {
        toast.error(result.error || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = (template: NotificationTemplate) => {
    setSendForm({
      ...sendForm,
      title: template.titleTemplate,
      message: template.messageTemplate,
      type: template.type,
    });
    setActiveTab(0); // Switch to Send tab
    toast.success('Template loaded into send form!');
  };

  // ========================================
  // ANALYTICS FUNCTIONS
  // ========================================

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const result = await adminNotificationService.getEnhancedStats();
      if (result.success && result.data) {
        setEnhancedStats(result.data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // HISTORY FUNCTIONS
  // ========================================

  const loadHistory = async () => {
    setLoading(true);
    try {
      const result = await adminNotificationService.getNotificationsList({
        page: listPage + 1,
        limit: listRowsPerPage,
      });


      if (result.success && result.data) {
        setNotificationsList(result.data.notifications || []);
        setListTotal(result.data.pagination?.total || 0);
        setListSummary(result.data.summary || { total: 0, read: 0, unread: 0, readRate: 0 });


        // Debug: Log first notification to see data structure
        if (result.data.notifications.length > 0) {
        }

        // Debug: Check read status distribution
        const readCount = result.data.notifications.filter(n => n.isRead).length;
        const readAtCount = result.data.notifications.filter(n => n.readAt).length;

        // Debug: Check for backend inconsistency (readAt exists but isRead is false)
        const inconsistent = result.data.notifications.filter(n => n.readAt && !n.isRead);
        if (inconsistent.length > 0) {
        }
      } else {
        console.error('❌ List load failed:', result.error);
        toast.error(result.error || 'Failed to load notifications list');
        // Set defaults on error
        setNotificationsList([]);
        setListTotal(0);
        setListSummary({ total: 0, read: 0, unread: 0, readRate: 0 });
      }
    } catch (error) {
      console.error('❌ Error loading list:', error);
      toast.error(`Failed to load list: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // Set defaults on exception
      setNotificationsList([]);
      setListTotal(0);
      setListSummary({ total: 0, read: 0, unread: 0, readRate: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryPageChange = (event: unknown, newPage: number) => {
    setListPage(newPage);
  };

  const handleHistoryRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setListRowsPerPage(parseInt(event.target.value, 10));
    setListPage(0);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    setMarkingAsRead(notificationId);

    // Optimistic update: immediately update UI
    setNotificationsList(prev =>
      prev.map(notif =>
        notif.id === notificationId
          ? { ...notif, isRead: true, readAt: new Date().toISOString() }
          : notif
      )
    );

    // Update summary counts optimistically
    setListSummary(prev => ({
      ...prev,
      read: prev.read + 1,
      unread: Math.max(0, prev.unread - 1),
      readRate: ((prev.read + 1) / prev.total) * 100,
    }));

    try {
      const result = await adminNotificationService.markAsRead(notificationId);

      if (result.success) {
        toast.success('Notification marked as read');
        // Reload the list to sync with backend
        await loadHistory();
      } else {
        // Revert optimistic update on failure
        toast.error(result.error || 'Failed to mark notification as read');
        await loadHistory(); // Reload to get correct state
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
      toast.error('Failed to mark notification as read');
      // Revert optimistic update on error
      await loadHistory(); // Reload to get correct state
    } finally {
      setMarkingAsRead(null);
    }
  };

  // Reload history when page or rows per page changes
  useEffect(() => {
    if (activeTab === 3) loadHistory();
  }, [listPage, listRowsPerPage]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon /> Multi-Channel Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage notification templates with Push, Email, and SMS channels. Configure priority levels and send custom notifications.
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Send Notification" icon={<SendIcon />} iconPosition="start" />
          <Tab label="Templates" icon={<EditIcon />} iconPosition="start" />
          <Tab label="Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab
            label="History"
            icon={<HistoryIcon />}
            iconPosition="start"
            disabled={false}
            sx={{
              '& .MuiTab-wrapper': {
                opacity: 0.6
              }
            }}
          />
        </Tabs>
      </Paper>

      {/* Tab 1: Send Notification */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Send to Single User Card */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, bgcolor: 'primary.50', border: 2, borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon /> Send to Single User
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                📱 Use Phone Number, Email, or User ID (provide at least one)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={singleUserForm.phoneNumber}
                    onChange={(e) => setSingleUserForm({ ...singleUserForm, phoneNumber: e.target.value })}
                    placeholder="+12176938032"
                    helperText="Include country code"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={singleUserForm.email}
                    onChange={(e) => setSingleUserForm({ ...singleUserForm, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={singleUserForm.userId}
                    onChange={(e) => setSingleUserForm({ ...singleUserForm, userId: e.target.value })}
                    placeholder="user-abc-123"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title *"
                    value={singleUserForm.title}
                    onChange={(e) => setSingleUserForm({ ...singleUserForm, title: e.target.value })}
                    placeholder="Notification title"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={singleUserForm.type}
                      label="Type"
                      onChange={(e) => setSingleUserForm({ ...singleUserForm, type: e.target.value })}
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="promotion">Promotion</MenuItem>
                      <MenuItem value="system">System</MenuItem>
                      <MenuItem value="booking">Booking</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Message *"
                    value={singleUserForm.message}
                    onChange={(e) => setSingleUserForm({ ...singleUserForm, message: e.target.value })}
                    placeholder="Your notification message..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={sendingSingleUser ? <CircularProgress size={20} /> : <SendIcon />}
                    onClick={handleSendToSingleUser}
                    disabled={sendingSingleUser || !singleUserForm.title || !singleUserForm.message}
                    size="large"
                  >
                    {sendingSingleUser ? 'Sending...' : 'Send to User'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }}>
              <Chip label="OR" />
            </Divider>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon /> Send to Multiple Users
              </Typography>

              <TextField
                fullWidth
                label="Notification Title *"
                value={sendForm.title}
                onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                placeholder="Enter a compelling title..."
                sx={{ mb: 2, mt: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notification Message *"
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                placeholder="Write your message here..."
                sx={{ mb: 3 }}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Target Audience
              </Typography>

              <RadioGroup
                value={sendForm.targetType}
                onChange={(e) => setSendForm({ ...sendForm, targetType: e.target.value as any })}
              >
                <FormControlLabel value="all_hosts" control={<Radio />} label="All Hosts" />
                <FormControlLabel value="all_renters" control={<Radio />} label="All Renters" />
                <FormControlLabel value="specific" control={<Radio />} label="Specific Users (comma-separated IDs)" />
              </RadioGroup>

              {sendForm.targetType === 'specific' && (
                <TextField
                  fullWidth
                  label="User IDs (comma-separated)"
                  value={sendForm.userIds}
                  onChange={(e) => setSendForm({ ...sendForm, userIds: e.target.value })}
                  placeholder="user-id-1, user-id-2, user-id-3"
                  sx={{ mt: 2 }}
                />
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handleSendNotification}
                  disabled={sending || !sendForm.title || !sendForm.message}
                  size="large"
                >
                  {sending ? 'Sending...' : 'Send Notification'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setPreviewOpen(true)}
                >
                  Preview
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Estimated Recipients
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {getEstimatedRecipients()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Will receive notification
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Alert severity="info">
              <Typography variant="body2">
                💡 <strong>Tip:</strong> Use templates for consistent messaging. Switch to the Templates tab to manage them.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Templates */}
      <TabPanel value={activeTab} index={1}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {templates.length} notification templates available
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleInitializeTemplates}
                  disabled={initializing || loading}
                >
                  {initializing ? 'Initializing...' : 'Initialize Templates'}
                </Button>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadTemplates}
                  disabled={loading || initializing}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {templates.map((template) => {
                // Priority badge colors
                const getPriorityColor = (priority: string) => {
                  switch (priority) {
                    case 'urgent': return 'error';
                    case 'high': return 'warning';
                    case 'normal': return 'info';
                    case 'low': return 'default';
                    default: return 'default';
                  }
                };

                return (
                  <Grid item xs={12} md={6} key={template.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">{template.type.replace(/_/g, ' ').toUpperCase()}</Typography>
                              <Switch
                                checked={template.isActive}
                                size="small"
                                onChange={() => handleEditTemplate(template)}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <Chip label={template.category} size="small" color="primary" variant="outlined" />
                              {template.priority && (
                                <Chip
                                  label={template.priority.toUpperCase()}
                                  size="small"
                                  color={getPriorityColor(template.priority) as any}
                                />
                              )}
                            </Box>
                            {/* Channel Indicators */}
                            {template.channels && (
                              <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                                {template.channels.push && (
                                  <Chip label="📱 Push" size="small" variant="outlined" color="success" />
                                )}
                                {template.channels.email && (
                                  <Chip label="📧 Email" size="small" variant="outlined" color="info" />
                                )}
                                {template.channels.sms && (
                                  <Chip label="💬 SMS" size="small" variant="outlined" color="warning" />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Title:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {template.titleTemplate}
                        </Typography>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Message:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }} noWrap>
                          {template.messageTemplate.substring(0, 100)}...
                        </Typography>

                        {/* Show email/SMS template status */}
                        {template.channels?.email && template.emailSubjectTemplate && (
                          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                            <Typography variant="caption">
                              Email template configured
                            </Typography>
                          </Alert>
                        )}
                        {template.channels?.sms && template.smsTemplate && (
                          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                            <Typography variant="caption">
                              SMS template configured
                            </Typography>
                          </Alert>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          Variables: {template.variables.map(v => `{${v}}`).join(', ')}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditTemplate(template)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={() => handleUseTemplate(template)}
                          >
                            Use Template
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Tab 3: Analytics */}
      <TabPanel value={activeTab} index={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : enhancedStats ? (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">📊 Enhanced Notification Analytics</Typography>
              <Button startIcon={<RefreshIcon />} onClick={loadAnalytics}>
                Refresh
              </Button>
            </Box>

            {/* Overall Metrics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SendIcon color="primary" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {enhancedStats.overall.totalSent.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Sent
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VisibilityIcon color="success" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {enhancedStats.overall.totalRead.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Read
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {enhancedStats.overall.uniqueRecipients.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Unique Recipients
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TrendingUp color="warning" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {(typeof enhancedStats.overall.readRate === 'number'
                            ? enhancedStats.overall.readRate
                            : parseFloat(enhancedStats.overall.readRate) || 0).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Read Rate
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={typeof enhancedStats.overall.readRate === 'number'
                        ? enhancedStats.overall.readRate
                        : parseFloat(enhancedStats.overall.readRate) || 0}
                      sx={{ mt: 2 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Time Ranges */}
            {enhancedStats.timeRanges && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Last 24 Hours</Typography>
                      <Typography variant="h5">{(enhancedStats.timeRanges.last24h || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Last 7 Days</Typography>
                      <Typography variant="h5">{(enhancedStats.timeRanges.last7d || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Last 30 Days</Typography>
                      <Typography variant="h5">{(enhancedStats.timeRanges.last30d || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Notifications by Type */}
            {enhancedStats.byType && Object.keys(enhancedStats.byType).length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Notifications by Type (with Read Rates)
                </Typography>
                <List>
                  {Object.entries(enhancedStats.byType).map(([type, data]) => {
                    const readRate = typeof data.readRate === 'number' ? data.readRate : parseFloat(data.readRate) || 0;
                    return (
                      <ListItem key={type}>
                        <ListItemText
                          primary={type.replace(/_/g, ' ').toUpperCase()}
                          secondary={`${data.total || 0} sent • ${data.read || 0} read • ${readRate.toFixed(1)}% read rate`}
                        />
                        <Stack direction="row" spacing={1}>
                          <Chip label={`${data.total || 0} sent`} color="primary" size="small" />
                          <Chip label={`${readRate.toFixed(1)}%`} color="success" size="small" />
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}

            {/* Top Recipients */}
            {enhancedStats.topRecipients && enhancedStats.topRecipients.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Recipients
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Received</TableCell>
                        <TableCell>Read</TableCell>
                        <TableCell>Read Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {enhancedStats.topRecipients.map((recipient) => {
                        const readRate = typeof recipient.readRate === 'number' ? recipient.readRate : parseFloat(recipient.readRate) || 0;
                        return (
                          <TableRow key={recipient.userId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{recipient.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{recipient.email}</Typography>
                            </TableCell>
                            <TableCell>{recipient.totalReceived || 0}</TableCell>
                            <TableCell>{recipient.totalRead || 0}</TableCell>
                            <TableCell>
                              <Chip label={`${readRate.toFixed(1)}%`} color="success" size="small" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        ) : (
          <Alert severity="info">No analytics data available</Alert>
        )}
      </TabPanel>

      {/* Tab 4: History - Now using Enhanced Notifications List */}
      <TabPanel value={activeTab} index={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">📜 Notification List (with Real Read Data)</Typography>
              <Button startIcon={<RefreshIcon />} onClick={loadHistory}>
                Refresh
              </Button>
            </Box>

            {/* Backend Issue Warning */}
            {listSummary.total > 0 && listSummary.read === 0 && (
              <Alert severity="warning" sx={{ m: 2 }}>
                <strong>⚠️ Backend Issue Detected:</strong> Read tracking may not be working properly.
                All {listSummary.total} notifications show as unread. This needs backend investigation to:
                <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                  <li>Ensure notification reads are being tracked in the database</li>
                  <li>Verify the /api/admin/notifications/list endpoint returns accurate read status</li>
                </ul>
              </Alert>
            )}

            {/* Summary Stats */}
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                  <Typography variant="h6">{listSummary.total || 0}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Read</Typography>
                  <Typography variant="h6" color={listSummary.read === 0 && listSummary.total > 0 ? 'warning.main' : 'success.main'}>
                    {listSummary.read || 0}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Unread</Typography>
                  <Typography variant="h6" color="warning.main">{listSummary.unread || 0}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Read Rate</Typography>
                  <Typography variant="h6" color="info.main">
                    {(typeof listSummary.readRate === 'number' ? listSummary.readRate : parseFloat(listSummary.readRate) || 0).toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Read</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notificationsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No notifications yet. Send your first notification to see it here!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notificationsList.map((item) => {
                      // Handle date parsing
                      const getFormattedDate = () => {
                        const dateStr = item.sentAt || item.createdAt;
                        if (!dateStr) return 'N/A';

                        try {
                          const date = new Date(dateStr);
                          if (isNaN(date.getTime())) return dateStr;
                          return date.toLocaleString();
                        } catch (e) {
                          return dateStr;
                        }
                      };

                      // Validate if userId looks like a real ID (not notification content)
                      const isValidUserId = (id: string) => {
                        if (!id) return false;
                        // Real user IDs are typically UUIDs, ObjectIds, or numeric IDs
                        // They should NOT contain spaces or be full sentences
                        const hasSpaces = id.includes(' ');
                        const isAllCaps = id === id.toUpperCase() && id.length > 10; // Likely a title/message
                        return !hasSpaces && !isAllCaps;
                      };

                      const validUserId = item.userId && isValidUserId(item.userId) ? item.userId : null;

                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.userName || item.userEmail || (validUserId ? `User ${validUserId.substring(0, 8)}...` : 'Unknown User')}
                            </Typography>
                            {item.userEmail && item.userName && item.userEmail !== item.userName && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {item.userEmail}
                              </Typography>
                            )}
                            {!item.userName && !item.userEmail && validUserId && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                ⚠️ User ID: {validUserId}
                              </Typography>
                            )}
                            {!item.userName && !item.userEmail && !validUserId && (
                              <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                ⚠️ User data missing from backend
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                              {item.message?.substring(0, 60) || 'No message'}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={item.type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              size="small"
                              color={
                                item.status === 'delivered' ? 'success' :
                                item.status === 'failed' ? 'error' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {/* Handle backend inconsistency: if readAt exists, treat as read */}
                            {item.isRead || item.readAt ? (
                              <>
                                <Chip icon={<SuccessIcon />} label="Read" size="small" color="success" />
                                {item.readAt && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {new Date(item.readAt).toLocaleString()}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Chip label="Unread" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {getFormattedDate()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {/* Handle backend inconsistency: if readAt exists, treat as read */}
                            {!(item.isRead || item.readAt) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SuccessIcon />}
                                onClick={() => handleMarkAsRead(item.id)}
                                disabled={markingAsRead === item.id}
                              >
                                {markingAsRead === item.id ? 'Marking...' : 'Mark as Read'}
                              </Button>
                            )}
                            {(item.isRead || item.readAt) && (
                              <Typography variant="caption" color="success.main">
                                ✓ Already Read
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={listTotal || 0}
              page={listPage}
              onPageChange={handleHistoryPageChange}
              rowsPerPage={listRowsPerPage}
              onRowsPerPageChange={handleHistoryRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Paper>
        )}
      </TabPanel>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Notification Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <NotificationsIcon fontSize="small" color="primary" />
              <Typography variant="caption" color="text.secondary">
                PolarRide
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {sendForm.title || 'Notification Title'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sendForm.message || 'Notification message will appear here...'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Template: {selectedTemplate?.type.replace(/_/g, ' ').toUpperCase()}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Available Variables:</strong> {selectedTemplate.variables.map(v => `{${v}}`).join(', ')}
                </Typography>
              </Alert>

              {/* Basic Fields */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Push Notification Template
              </Typography>

              <TextField
                fullWidth
                label="Title Template"
                value={editForm.titleTemplate}
                onChange={(e) => setEditForm({ ...editForm, titleTemplate: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Use {variable} syntax for dynamic content"
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message Template"
                value={editForm.messageTemplate}
                onChange={(e) => setEditForm({ ...editForm, messageTemplate: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Use {variable} syntax for dynamic content"
              />

              <TextField
                fullWidth
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                sx={{ mb: 3 }}
              />

              <Divider sx={{ my: 3 }} />

              {/* Multi-Channel Configuration */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Multi-Channel Configuration
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={editForm.priority}
                  label="Priority"
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'low' | 'normal' | 'high' | 'urgent' })}
                >
                  <MenuItem value="low">Low (Push only)</MenuItem>
                  <MenuItem value="normal">Normal (Push + Email)</MenuItem>
                  <MenuItem value="high">High (Push + Email + SMS)</MenuItem>
                  <MenuItem value="urgent">Urgent (All channels + alerts)</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Channels
              </Typography>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.channels.push}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        channels: { ...editForm.channels, push: e.target.checked }
                      })}
                    />
                  }
                  label="Push Notification (Always recommended)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.channels.email}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        channels: { ...editForm.channels, email: e.target.checked }
                      })}
                    />
                  }
                  label="Email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.channels.sms}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        channels: { ...editForm.channels, sms: e.target.checked }
                      })}
                    />
                  }
                  label="SMS (Costs money - use sparingly)"
                />
              </Box>

              {/* Email Template Fields */}
              {editForm.channels.email && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Email Template
                  </Typography>

                  <TextField
                    fullWidth
                    label="Email Subject Template"
                    value={editForm.emailSubjectTemplate}
                    onChange={(e) => setEditForm({ ...editForm, emailSubjectTemplate: e.target.value })}
                    sx={{ mb: 2 }}
                    helperText="Use {variable} syntax for dynamic content"
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Email HTML Template"
                    value={editForm.emailHtmlTemplate}
                    onChange={(e) => setEditForm({ ...editForm, emailHtmlTemplate: e.target.value })}
                    sx={{ mb: 2 }}
                    helperText="HTML content for email body. Use {variable} syntax for dynamic content"
                  />
                </>
              )}

              {/* SMS Template Field */}
              {editForm.channels.sms && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    SMS Template
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="SMS Template"
                    value={editForm.smsTemplate}
                    onChange={(e) => setEditForm({ ...editForm, smsTemplate: e.target.value })}
                    sx={{ mb: 2 }}
                    helperText={`Keep under 160 characters (current: ${editForm.smsTemplate.length}/160). Use {variable} syntax for dynamic content`}
                    error={editForm.smsTemplate.length > 160}
                  />
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                }
                label="Active (enabled for automatic sending)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : undefined}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
